import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { UNIT_TYPES, parseQty } from '@pos/core';
import { applyLocalChanges } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { requestSync } from '../../services/sync.ts';
import { Alert, Button, Card, Field, Input, PageHeader, Select, Spinner, Toggle, errorMessage } from '../../ui/index.tsx';
import { MoneyInput } from '../../ui/MoneyInput.tsx';
import { ProductPicker, type PickedVariant } from '../products/ProductPicker.tsx';
import { useCatalog, useMoney } from '../products/data.ts';
import { buildPurchaseChanges, lineCost } from './changes.ts';
import { useStockContext } from './data.ts';

interface Line {
  picked: PickedVariant;
  qtyText: string;
  unitCost: number | null;
}

/** "2026-10-03T15:30" en hora local, para un <input type="datetime-local">. */
function localDateTime(date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function PurchasePage() {
  const { can, writer } = useSession();
  const catalog = useCatalog();
  const money = useMoney();
  const navigate = useNavigate();
  const context = useStockContext();
  const withCosts = can('view_costs');

  const [supplierId, setSupplierId] = useState('');
  const [invoice, setInvoice] = useState('');
  const [when, setWhen] = useState(localDateTime);
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [updateCosts, setUpdateCosts] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!catalog) {
    return (
      <div className="flex justify-center py-12 text-slate-500">
        <Spinner />
      </div>
    );
  }

  const add = (picked: PickedVariant) => {
    setLines((list) => {
      const existing = list.findIndex((l) => l.picked.variant.id === picked.variant.id);
      if (existing >= 0) {
        // Escanear dos veces el mismo producto suma una unidad.
        return list.map((l, i) => {
          if (i !== existing) return l;
          const current = parseQty(l.qtyText, picked.item.product.unit_type) ?? 0;
          return { ...l, qtyText: String((current + 1000) / 1000).replace('.', ',') };
        });
      }
      return [...list, { picked, qtyText: '1', unitCost: catalog.costs.get(picked.variant.id) ?? null }];
    });
  };

  const parsed = lines.map((l) => ({ ...l, qty: parseQty(l.qtyText, l.picked.item.product.unit_type) }));
  const total = withCosts && parsed.every((l) => l.qty !== null && l.unitCost !== null)
    ? parsed.reduce((sum, l) => sum + lineCost(l.unitCost!, l.qty!), 0)
    : null;

  const save = async () => {
    if (parsed.some((l) => l.qty === null || l.qty === 0)) return setError('Revisá las cantidades: hay alguna vacía o inválida.');
    setBusy(true);
    setError(null);
    try {
      const { changes } = buildPurchaseChanges(
        {
          supplierId: supplierId || null,
          invoiceNumber: invoice,
          occurredAt: new Date(when).toISOString(),
          notes,
          withCosts,
          updateCosts,
          knownCosts: catalog.costs,
          lines: parsed.map((l) => ({ variantId: l.picked.variant.id, qtyMilli: l.qty!, unitCost: l.unitCost })),
        },
        context(),
      );
      await applyLocalChanges(db, changes, writer);
      requestSync(0);
      navigate('/stock');
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4 pb-8">
      <PageHeader
        title="Cargar compra"
        description={withCosts ? 'Lo que entró del proveedor. El stock sube y, si cargás el costo, se actualiza el del producto.' : 'Lo que entró del proveedor. El stock sube al guardar.'}
        actions={
          <Link to="/stock">
            <Button variant="secondary">Volver</Button>
          </Link>
        }
      />

      <Card className="grid gap-4 sm:grid-cols-3">
        <Field label="Proveedor">
          {(id) => (
            <Select id={id} value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
              <option value="">Sin proveedor</option>
              {catalog.suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Nº de factura o remito">{(id) => <Input id={id} maxLength={40} value={invoice} onChange={(e) => setInvoice(e.target.value)} />}</Field>
        <Field label="Fecha">{(id) => <Input id={id} type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />}</Field>
      </Card>

      <Card className="space-y-3">
        <h2 className="font-semibold">Productos</h2>
        <ProductPicker catalog={catalog} onPick={add} autoFocus />
        {lines.length === 0 ? (
          <p className="py-4 text-center text-sm text-slate-500">Buscá o escaneá los productos que llegaron.</p>
        ) : (
          <div className="divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200">
            {parsed.map((l, i) => {
              const unit = UNIT_TYPES[l.picked.item.product.unit_type];
              return (
                <div key={l.picked.variant.id} className="grid items-center gap-2 p-3 sm:grid-cols-[1fr_7rem_9rem_auto]">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{l.picked.label}</p>
                    {withCosts && l.qty !== null && l.unitCost !== null && (
                      <p className="text-xs text-slate-500">Subtotal {money(lineCost(l.unitCost, l.qty))}</p>
                    )}
                    {withCosts && l.unitCost !== null && l.unitCost > l.picked.variant.price && (
                      <p className="text-xs font-medium text-amber-700">
                        Ojo: el costo es mayor que el precio de venta ({money(l.picked.variant.price)}). ¿Es el costo de la caja o del pack en vez del de {unit.symbol === 'u' ? 'una unidad' : `1 ${unit.symbol}`}?
                      </p>
                    )}
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <Input
                      inputMode="decimal"
                      aria-label="Cantidad"
                      invalid={l.qty === null}
                      value={l.qtyText}
                      onChange={(e) => setLines((list) => list.map((x, j) => (j === i ? { ...x, qtyText: e.target.value } : x)))}
                    />
                    <span className="text-slate-500">{unit.symbol}</span>
                  </label>
                  {withCosts ? (
                    <MoneyInput
                      aria-label={`Costo por ${unit.symbol}`}
                      placeholder={`Costo x ${unit.symbol}`}
                      value={l.unitCost}
                      onChange={(c) => setLines((list) => list.map((x, j) => (j === i ? { ...x, unitCost: c } : x)))}
                    />
                  ) : (
                    <span />
                  )}
                  <Button variant="dangerGhost" size="sm" onClick={() => setLines((list) => list.filter((_, j) => j !== i))}>
                    Quitar
                  </Button>
                </div>
              );
            })}
          </div>
        )}
        {withCosts && lines.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Toggle checked={updateCosts} onChange={setUpdateCosts} label="Actualizar el costo de los productos" description="El costo de esta compra pasa a ser el costo del producto." />
            <p className="text-lg font-semibold">Total: {total === null ? '—' : money(total)}</p>
          </div>
        )}
      </Card>

      <Card>
        <Field label="Notas (opcional)">{(id) => <Input id={id} maxLength={500} value={notes} onChange={(e) => setNotes(e.target.value)} />}</Field>
      </Card>

      {error && <Alert tone="error">{error}</Alert>}
      <div className="flex justify-end gap-2">
        <Link to="/stock">
          <Button variant="secondary">Cancelar</Button>
        </Link>
        <Button size="lg" onClick={save} loading={busy} disabled={lines.length === 0}>
          Guardar compra
        </Button>
      </div>
    </div>
  );
}
